/**
 * FactoryCostPage — dedicated page cho `/factory/<code>/data/bom-cost`.
 *
 * Master data Batch Cost (table `cost_item` join `cost_group` via view
 * `v_cost_item_master`). Layout y chang BOM: TableVirtuoso + app-dataframe
 * toolbar + filter popovers + columns menu + sortable headers + BrandState.
 */
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import PaidIcon from '@mui/icons-material/Paid'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha, type SxProps, type Theme } from '@mui/material/styles'
import { useMemo, useState } from 'react'
import { TableVirtuoso } from 'react-virtuoso'

import BrandState from '@/components/BrandState'
import ConfirmDialog from '@/components/ConfirmDialog'
import ResizeHandle from '@/components/ResizeHandle'
import {
  type ColumnFilterAnchorPosition,
  type ColumnFilterKind,
  ColumnFilterIcon,
  ColumnFilterPopover,
  FilterButton,
  filterBarBtnSx,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
} from '@/components/dataList'
import { useColumnWidths } from '@/hooks/useColumnWidths'
import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useCreateFactoryCostMutation,
  useDeleteFactoryCostMutation,
  useGetFactoryCostDetailQuery,
  useGetFactoryCostListQuery,
  useGetFactoryCostOptionsQuery,
  useUpdateFactoryCostMutation,
  type CostListRow,
  type CostSavePayload,
} from '@/store/api/factoryCostApi'
import { FuturesColumnsMenu } from '@/pages/futures/FuturesColumnsMenu'
import { usePreferenceColumns } from '@/utils/datasheetColumns/usePreferenceColumns'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortKind,
  type TableSortState,
  toggleTableSort,
} from '@/utils/tableSort'

import CostFormDialog from './CostFormDialog'

const actionToolbarBtnSx: SxProps<Theme> = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

type ColumnDef = {
  key: string
  label: string
  kind?: TableSortKind
  align?: 'right'
  filter?: ColumnFilterKind
}

const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'cost_item_code', label: 'Item Code' },
  { key: 'display_code', label: 'Display Code' },
  { key: 'cost_group_name_en', label: 'Group' },
  { key: 'cost_item_name_en', label: 'Item Name' },
  { key: 'cost_item_name_vi', label: 'Item Name (VN)' },
  { key: 'unit_price', label: 'Unit Price', kind: 'currency', align: 'right' },
  { key: 'currency', label: 'Currency' },
  { key: 'price_unit', label: 'Unit' },
  { key: 'calc_method', label: 'Calc Method' },
  { key: 'valid_from', label: 'Valid From', kind: 'date' },
  { key: 'valid_to', label: 'Valid To', kind: 'date' },
  { key: 'vendor_name', label: 'Vendor' },
  { key: 'vendor_contract_no', label: 'Vendor Contract' },
  { key: 'description', label: 'Description' },
  { key: 'updated_date', label: 'Updated Date', kind: 'date' },
  { key: 'updated_by', label: 'Updated Person' },
]

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatAccessDate = (value: unknown): string => {
  if (value == null || value === '') return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  return `${day}-${month}-${d.getFullYear()}`
}

const formatCurrency = (value: unknown): string => {
  if (value == null || value === '') return ''
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
}

const distinctValues = (rows: CostListRow[], key: string): string[] => {
  const values = new Set<string>()
  for (const row of rows) {
    const value = (row as Record<string, unknown>)[key]
    if (value == null) continue
    const text = String(value).trim()
    if (text) values.add(text)
  }
  return [...values].sort((a, b) => a.localeCompare(b))
}

const cellValue = (row: CostListRow, col: ColumnDef): string => {
  const val = (row as Record<string, unknown>)[col.key]
  if (val == null || val === '') return ''
  if (col.kind === 'date') return formatAccessDate(val)
  if (col.kind === 'currency') return formatCurrency(val)
  if (typeof val === 'boolean') return val ? 'Yes' : 'No'
  return String(val)
}

interface FactoryCostPageProps {
  factoryCode: string
  /** Back-to-hub callback. Khi user trở về `/factory/<code>/data`. */
  onBackToHub: () => void
  canEdit: boolean
}

type DialogState =
  | { kind: 'closed' }
  | { kind: 'new' }
  | { kind: 'edit'; costItemId: number }

interface PendingDelete {
  costItemId: number
  costItemCode: string
  costGroupCode: string
}

const ALL_ROWS_PAGE_SIZE = -1

export default function FactoryCostPage({
  factoryCode,
  onBackToHub,
  canEdit,
}: FactoryCostPageProps) {
  const snack = useSnackbar()

  const [costGroupFilter, setCostGroupFilter] = useState('')

  const list = useGetFactoryCostListQuery({ factoryCode })
  const options = useGetFactoryCostOptionsQuery(factoryCode)

  const { selectedId, setSelectedId, toggleSelect } = useRowSelection<number>()
  const [dialog, setDialog] = useState<DialogState>({ kind: 'closed' })
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null)

  const [createCost, { isLoading: creating }] = useCreateFactoryCostMutation()
  const [updateCost, { isLoading: updating }] = useUpdateFactoryCostMutation()
  const [deleteCost, { isLoading: deleting }] = useDeleteFactoryCostMutation()

  const editDetail = useGetFactoryCostDetailQuery(
    dialog.kind === 'edit'
      ? { factoryCode, costItemId: dialog.costItemId }
      : { factoryCode, costItemId: 0 },
    { skip: dialog.kind !== 'edit' },
  )

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(ALL_ROWS_PAGE_SIZE)

  const { getWidth, startResize } = useColumnWidths('factory.cost.list')

  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<string> | null>(null)

  const filterPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: string
    position: ColumnFilterAnchorPosition
  } | null>(null)

  const columnsCtrl = usePreferenceColumns<ColumnDef>({
    scope: 'factory',
    prefKey: 'columns',
    subKey: 'cost',
    allColumns: TABLE_COLUMNS,
    widthsTableKey: 'factory.cost.list',
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl

  const baseRows = useMemo(() => list.data?.items ?? [], [list.data])

  const filteredRows = useMemo(
    () =>
      baseRows.filter((row) => {
        if (!rowMatchesSearch(row, searchTerm, TABLE_COLUMNS)) return false
        if (costGroupFilter && row.cost_group_code !== costGroupFilter) return false
        if (!rowMatchesAllColumnFilters(row, columnFilters.filters)) return false
        return true
      }),
    [baseRows, searchTerm, costGroupFilter, columnFilters.filters],
  )

  const hasActiveFilters =
    !!searchTerm.trim() || !!costGroupFilter || columnFilters.hasAny
  const activeFilterCount =
    (searchTerm.trim() ? 1 : 0) + (costGroupFilter ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    setCostGroupFilter('')
    columnFilters.clearAll()
    setPage(0)
    filterPopover.close()
  }

  const popoverColumn = useMemo(
    () => (colPopover ? TABLE_COLUMNS.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover],
  )
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(baseRows, colPopover.key)
        : undefined,
    [popoverKind, colPopover, baseRows],
  )

  const sortedRows = useMemo(
    () => sortTableRows(filteredRows, sortState, TABLE_COLUMNS),
    [filteredRows, sortState],
  )

  const rows = useMemo(() => {
    if (rowsPerPage === -1) return sortedRows
    const start = page * rowsPerPage
    return sortedRows.slice(start, start + rowsPerPage)
  }, [page, rowsPerPage, sortedRows])

  const costGroupOptions = useMemo(() => options.data?.costGroups ?? [], [options.data?.costGroups])

  const minTableWidth = Math.max(960, visibleColumns.length * 120)
  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}:${visibleColumns.length}`

  const toggleSort = (key: string) => {
    setSortState((current) => toggleTableSort(current, key))
    setPage(0)
  }

  const selectedRow = useMemo(
    () => baseRows.find((r) => r.cost_item_id === selectedId) ?? null,
    [baseRows, selectedId],
  )

  const handleSave = async (payload: CostSavePayload) => {
    if (!canEdit) {
      snack.error('Read-only — you do not have edit permission for this factory.')
      return
    }
    try {
      if (dialog.kind === 'new') {
        await createCost({ factoryCode, body: payload }).unwrap()
        snack.success('Cost item created')
      } else if (dialog.kind === 'edit') {
        await updateCost({
          factoryCode,
          costItemId: dialog.costItemId,
          body: payload,
        }).unwrap()
        snack.success('Cost item updated')
      }
      setDialog({ kind: 'closed' })
    } catch (err) {
      const msg =
        err && typeof err === 'object' && 'data' in err
          ? String((err as { data?: { detail?: string } }).data?.detail ?? 'Save failed')
          : 'Save failed'
      snack.error(msg)
    }
  }

  const handleConfirmDelete = async () => {
    if (!canEdit) {
      snack.error('Read-only — you do not have edit permission for this factory.')
      return
    }
    if (!pendingDelete) return
    try {
      await deleteCost({
        factoryCode,
        costItemId: pendingDelete.costItemId,
        costGroupCode: pendingDelete.costGroupCode,
      }).unwrap()
      snack.success('Cost item deleted')
      if (selectedId === pendingDelete.costItemId) setSelectedId(null)
      setPendingDelete(null)
    } catch (err) {
      const msg =
        err && typeof err === 'object' && 'data' in err
          ? String((err as { data?: { detail?: string } }).data?.detail ?? 'Delete failed')
          : 'Delete failed'
      snack.error(msg)
    }
  }

  const handleEditClick = () => {
    if (!canEdit) return
    if (!selectedRow) {
      snack.warning('Please choose a record!')
      return
    }
    setDialog({ kind: 'edit', costItemId: selectedRow.cost_item_id })
  }

  const handleDeleteClick = () => {
    if (!canEdit) return
    if (!selectedRow) {
      snack.warning('Please choose a record!')
      return
    }
    setPendingDelete({
      costItemId: selectedRow.cost_item_id,
      costItemCode: selectedRow.cost_item_code,
      costGroupCode: selectedRow.cost_group_code,
    })
  }

  const saving = creating || updating

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Action + filter bar */}
      <Stack
        direction="row"
        flexWrap="wrap"
        useFlexGap
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 0.5 }}
      >
        <Stack direction="row" spacing={0.375} flexWrap="wrap" useFlexGap alignItems="center" sx={{ minWidth: 0 }}>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<ArrowBackIcon sx={{ fontSize: 14 }} />}
            onClick={onBackToHub}
            sx={filterBarBtnSx}
          >
            Datasets
          </Button>
          {/* Page title — solid accent pill (icon + label trên cùng background
              accent color, text trắng) để user vào page là nhận ra context ngay. */}
          <Stack
            direction="row"
            spacing={0.625}
            alignItems="center"
            sx={{
              mx: 0.5,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              backgroundColor: '#5a7a4e',
              color: '#fff',
              boxShadow: `0 1px 3px ${alpha('#5a7a4e', 0.45)}`,
            }}
          >
            <PaidIcon sx={{ fontSize: '0.95rem', color: '#fff' }} />
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 800,
                fontSize: '0.85rem',
                color: '#fff',
                textTransform: 'uppercase',
                letterSpacing: 0.6,
                whiteSpace: 'nowrap',
                lineHeight: 1.1,
              }}
            >
              Batch Cost
            </Typography>
          </Stack>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25, fontWeight: 400 }}>
            Filter
          </Typography>
          <FilterButton
            label="Search"
            active={!!searchTerm.trim()}
            onClick={(e) => filterPopover.open('search', e)}
          />
          {hasActiveFilters && (
            <Button size="small" variant="text" color="secondary" onClick={clearAllFilters} sx={filterBarBtnSx}>
              Clear All{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => openColMenu(e.currentTarget)}
            sx={filterBarBtnSx}
          >
            Columns
          </Button>
        </Stack>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel sx={{ fontSize: '0.72rem' }}>Cost Group</InputLabel>
            <Select
              label="Cost Group"
              value={costGroupFilter}
              onChange={(e) => { setCostGroupFilter(String(e.target.value)); setPage(0) }}
              sx={{
                fontSize: '0.72rem',
                '& .MuiSelect-select': { py: 0.4, minHeight: '26px !important' },
              }}
            >
              <MenuItem value="" sx={{ fontSize: '0.78rem' }}>
                <em>All</em>
              </MenuItem>
              {costGroupOptions.map((g) => (
                <MenuItem key={g.cost_group_code} value={g.cost_group_code} sx={{ fontSize: '0.78rem' }}>
                  {g.cost_group_name_en || g.cost_group_code}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title={canEdit ? 'New' : 'Read-only — contact admin'}>
            <span>
              <Button
                variant="contained"
                size="small"
                color="success"
                sx={actionToolbarBtnSx}
                disabled={!canEdit || saving}
                onClick={() => setDialog({ kind: 'new' })}
              >
                New
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="outlined"
            size="small"
            color="warning"
            sx={actionToolbarBtnSx}
            disabled={!canEdit || !selectedRow || saving}
            onClick={handleEditClick}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="error"
            sx={actionToolbarBtnSx}
            disabled={!canEdit || !selectedRow || deleting}
            onClick={handleDeleteClick}
          >
            Delete
          </Button>
        </Stack>
      </Stack>

      <Divider sx={{ mb: 0.5 }} />

      {list.isError ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          Failed to load cost item list.
        </Alert>
      ) : null}

      <Box className="app-dataframe">
        {/* Toolbar — pagination + counts */}
        <Box
          className="app-dataframe-toolbar"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 0.5,
            flexWrap: 'wrap',
            minHeight: 24,
            py: 0,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
            {(list.isLoading || list.isFetching) && <CircularProgress size={15} />}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}
            >
              Total: {filteredRows.length}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}
            >
              Loaded: {baseRows.length}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={{
                flexShrink: 0,
                '& .MuiTablePagination-toolbar': { minHeight: 24, px: 0 },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.68rem' },
                '& .MuiTablePagination-select': { fontSize: '0.68rem' },
                '& .MuiTablePagination-actions': { transform: 'scale(0.86)', transformOrigin: 'center' },
              }}
            />
          </Stack>
        </Box>

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={filterPopover.close}
          maxWidth={320}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setPage(0) }}
              placeholder="Code, name, vendor..."
              label="Search Cost Item"
              onClear={filterPopover.close}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={() => setColPopover(null)}
          label={popoverColumn?.label ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[colPopover.key] : undefined}
          onChange={(v) => {
            if (!colPopover) return
            columnFilters.setFilter(colPopover.key, v)
            setPage(0)
          }}
          options={popoverOptions}
        />

        {/* Virtualized table */}
        <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
          {list.isError ? (
            <BrandState
              tone="error"
              title="Could not load cost item list"
              description="Please refresh the page or contact the system administrator if the issue continues."
            />
          ) : list.isLoading && baseRows.length === 0 ? (
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : rows.length === 0 ? (
            <BrandState
              title="No cost items found"
              description="Try adjusting the search or Calc Method filter above, or create a new cost item."
            />
          ) : (
            <TableVirtuoso
              key={gridKey}
              style={{ height: '100%' }}
              data={rows}
              fixedHeaderContent={() => (
                <TableRow>
                  {visibleColumns.map((col) => {
                    const w = getWidth(col.key)
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className="app-table-header-cell"
                        onClick={() => toggleSort(col.key)}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{ whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none', position: 'relative' }}
                      >
                        {col.label}
                        {getTableSortSuffix(sortState, col.key)}
                        <ColumnFilterIcon
                          active={columnFilters.isActive(col.key)}
                          onClick={(position) => setColPopover({ key: col.key, position })}
                          ariaLabel={`Filter ${col.label}`}
                        />
                        <ResizeHandle onMouseDown={(e) => startResize(col.key, e)} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              itemContent={(_: number, row: CostListRow) =>
                visibleColumns.map((col) => {
                  const val = cellValue(row, col)
                  const w = getWidth(col.key)
                  return (
                    <TableCell
                      key={col.key}
                      align={col.align}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      <Box
                        component="span"
                        className="app-table-cell-text"
                        title={val || '-'}
                        sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {val || '-'}
                      </Box>
                    </TableCell>
                  )
                })
              }
              components={{
                Table: (props: any) => (
                  <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />
                ),
                TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                TableRow: (props: any) => {
                  const idx = Number((props as any)['data-item-index'])
                  const row = Number.isFinite(idx) ? rows[idx] : null
                  const id = row?.cost_item_id ?? null
                  const isSelected = id != null && id === selectedId
                  return (
                    <TableRow
                      {...props}
                      hover
                      selected={isSelected}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (id != null) toggleSelect(id)
                      }}
                      onDoubleClick={() => {
                        if (!canEdit || row == null) return
                        setDialog({ kind: 'edit', costItemId: row.cost_item_id })
                      }}
                    />
                  )
                },
              }}
            />
          )}
        </Box>
      </Box>

      <CostFormDialog
        open={dialog.kind !== 'closed'}
        mode={dialog.kind === 'new' ? 'new' : 'edit'}
        detail={dialog.kind === 'edit' ? editDetail.data ?? null : null}
        detailLoading={dialog.kind === 'edit' && editDetail.isFetching}
        saving={saving}
        options={options.data ?? null}
        optionsLoading={options.isFetching}
        canEdit={canEdit}
        onClose={() => setDialog({ kind: 'closed' })}
        onSave={handleSave}
      />

      <ConfirmDialog
        open={pendingDelete != null}
        title="Delete Cost Item"
        message={
          pendingDelete
            ? `Delete cost item "${pendingDelete.costItemCode}"? `
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        danger
        loading={deleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDelete(null)}
      />

      <FuturesColumnsMenu controller={columnsCtrl} />
    </Box>
  )
}
